import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { HdBodygraphPlanetaryActivation } from '../hd-bodygraph.models';
import { getHdPlanetDisplayConfigByPlanetId } from '../planet-grid/hd-planet-display-config';
import { HdGateGraphNode } from './hd-gate-graph.models';
import {
  HdGateGraphFocusVm,
  buildHdGateGraphFocus,
  findHdGateGraphNodeAt,
} from './hd-gate-graph-focus';
import { HD_GATE_GRAPH_LAYOUT, buildHdGateGraphLayout } from './hd-gate-graph-layout';
import { buildHdGateGraphHighlight } from './hd-gate-graph-highlight';
import { buildHdGateGraphScene } from './hd-gate-graph-scene';
import {
  RALEWAY_CAP_HEIGHT,
  RALEWAY_DESCENT,
  chordWidth,
  wrapLabel,
  wrapLabelInCircle,
} from './hd-gate-graph-text';
import {
  HdGateGraphPaletteId,
  buildHdGateGraphPaletteVars,
  resolveHdGateGraphPalette,
} from './hd-gate-graph-palette';

/** Lettergroottes in viewBox-eenheden — zie de spec §6.3. */
const FONT = {
  gateNumber: 36,
  gateKeynote1: 8.4,
  gateKeynote2: 7.6,
  lineNumber: 17,
  lineKeynote: 5.4,
  colourNumber: 8,
  gateGlyph: 10,
  lineGlyph: 8,
} as const;

/**
 * Vrije ruimte tussen een lijn-keynote en de rand van de beige binnencirkel, aan
 * elke kant, in viewBox-eenheden. Daarbuiten ligt nog de ringmarge tot de
 * rand. Nagemeten over alle 768 lijn-keynotes (nl + en), met en zonder
 * glyph-rij: bij 1 valt geen enkele keynote af met een ellips, bij 1.5 wel
 * (poort 1.5, de langste van alle).
 */
const LINE_KEYNOTE_MARGIN = 1;

/** Grenzen van de zoom, als factor op de volledige spanwijdte. */
const ZOOM = { min: 0.05, max: 2.4, step: 1.14 } as const;

interface ViewBoxState {
  readonly cx: number;
  readonly cy: number;
  readonly span: number;
}

/** Eén regel tekst binnen een cirkel, met absolute positie in viewBox-eenheden. */
interface TextLineVm {
  readonly text: string;
  readonly y: number;
  readonly size: number;
  readonly dim: boolean;
}

/** Eén planeet-glyph in de poort- of lijncirkel. */
interface GlyphVm {
  readonly key: string;
  readonly char: string;
  readonly color: string;
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly size: number;
  /** Wit schijfje eronder — enkel gebruikt in de donkere poortcirkel. */
  readonly discY: number;
  readonly discR: number;
}

interface LineTextVm {
  readonly id: string;
  readonly x: number;
  readonly number: string;
  readonly numberY: number;
  readonly keynoteLines: readonly TextLineVm[];
  /** Volledige keynote — tooltip wanneer de tekst afgekapt wordt. */
  readonly keynote: string;
  readonly glyphs: readonly GlyphVm[];
}

/** Zijde-kleuren van de activatie-glyphs, zoals overal in de chart. */
const SIDE_GLYPH_COLORS: Readonly<Record<string, string>> = {
  personality: '#0e0e0e',
  design: '#c0392b',
};

/** Legt een rij glyphs gecentreerd rond `cx` op hoogte `y`. */
function layoutGlyphRow(
  entries: readonly { key: string; char: string; color: string; name: string }[],
  cx: number,
  y: number,
  size: number,
): GlyphVm[] {
  const gap = size * 1.55;
  const startX = cx - ((entries.length - 1) * gap) / 2;
  const round2 = (value: number): number => Math.round(value * 100) / 100;
  return entries.map((entry, index) => ({
    ...entry,
    x: round2(startX + index * gap),
    y: round2(y),
    size,
    // Het schijfje staat gecentreerd rond de optische midden van de glyph,
    // die boven zijn baseline zit.
    discY: round2(y - size * 0.33),
    discR: round2(size * 0.68),
  }));
}

/**
 * De radiale poort-visualisatie: één poort als boom van 1339 cirkels.
 *
 * Puur presentational — poort, keynotes, ramp en spreiding komen als inputs
 * binnen. De geometrie zit in {@link buildHdGateGraphLayout} en de batching in
 * {@link buildHdGateGraphScene}, allebei pure functies zonder Angular.
 *
 * Zie `docs/superpowers/specs/2026-07-30-hd-gate-radial-graph-design.md`.
 */
@Component({
  selector: 'hd-gate-graph',
  templateUrl: './hd-gate-graph.component.html',
  styleUrl: './hd-gate-graph.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[style]': 'paletteVars()' },
})
export class HdGateGraphComponent {
  readonly gate = input.required<number>();
  readonly keynote1 = input('');
  readonly keynote2 = input('');
  /** Zes lijn-keynotes, index 0 = lijn 1. Korter mag; ontbrekende blijven leeg. */
  readonly lineKeynotes = input<readonly string[]>([]);
  /** URL van het center-shape asset; weglaten toont geen icoon. */
  readonly centerIconUrl = input<string | undefined>(undefined);
  /** Vertaalde center-naam — tooltip op het icoon. */
  readonly centerName = input('');
  readonly palette = input<HdGateGraphPaletteId>('aarde');
  readonly spacing = input(HD_GATE_GRAPH_LAYOUT.spacing.default);
  /**
   * Planeet-activaties van deze poort. Elke activatie draagt lijn, kleur, toon
   * en basis en wijst dus exact één van de 1080 eindpunten aan.
   */
  readonly activations = input<readonly HdBodygraphPlanetaryActivation[]>([]);
  /**
   * Labels voor de schakelaar. De knop benoemt wat hij DOET, niet de huidige
   * staat: in highlight-modus lees je "Toon volledig", in de volledige weergave
   * "Highlight van chart".
   */
  readonly showFullLabel = input('');
  readonly showHighlightLabel = input('');

  protected readonly layoutConfig = HD_GATE_GRAPH_LAYOUT;
  protected readonly font = FONT;

  private readonly layout = computed(() => buildHdGateGraphLayout(this.spacing()));

  protected readonly scene = computed(() => buildHdGateGraphScene(this.layout()));

  protected readonly paletteVars = computed(() =>
    buildHdGateGraphPaletteVars(resolveHdGateGraphPalette(this.palette())));

  /**
   * Chart-highlight: enkel tonen wat de chart activeert. Default aan zodra er
   * activaties zijn. Je triggert deze weergave meestal vanuit een chart, maar
   * niet altijd — vandaar de schakelaar en de uitgeschakelde staat zonder
   * activaties (editor-klik, poort zonder activatie).
   */
  protected readonly highlightEnabled = signal(false);
  protected readonly canHighlight = computed(() => this.activations().length > 0);

  private readonly syncHighlightDefault = effect(() => {
    this.highlightEnabled.set(this.canHighlight());
  });

  protected readonly highlight = computed(() =>
    buildHdGateGraphHighlight(this.layout(), this.activations()));

  /**
   * Knoop waarover de muis staat. Voedt de focus-overlay: het voorouderpad naar
   * de poort plus de volledige deeltak van die knoop. Werkt op élk niveau —
   * lijn, kleur, toon of basis.
   */
  private readonly hoveredNode = signal<HdGateGraphNode | undefined>(undefined);

  private readonly focusCache = new Map<string, HdGateGraphFocusVm>();

  protected readonly focus = computed<HdGateGraphFocusVm | undefined>(() => {
    const node = this.hoveredNode();
    if (node === undefined) return undefined;
    const cached = this.focusCache.get(node.id);
    if (cached !== undefined) return cached;
    const built = buildHdGateGraphFocus(node);
    this.focusCache.set(node.id, built);
    return built;
  });

  /** Hover-focus geldt in de volledige weergave; de chart-highlight gaat voor. */
  protected readonly focusActive = computed(() =>
    !this.highlightActive() && this.focus() !== undefined);

  /** Beide overlays dimmen dezelfde basistekening. */
  protected readonly dimmed = computed(() => this.highlightActive() || this.focusActive());

  /**
   * De lijncirkel op het voorouderpad van de hover, met haar tekst — zodat ze
   * onverzwakt hertekend kan worden bovenop de gedimde basistekening.
   */
  protected readonly focusLines = computed(() => {
    const focused = this.focus();
    if (focused === undefined) return [];
    // Zowel de lijn op het voorouderpad als de lijnen ín de deeltak (hover je
    // op de poort, dan zijn dat alle zes) moeten hun nummer en keynote houden.
    const ids = new Set([
      ...focused.ancestors.filter(node => node.layer === 1).map(node => node.id),
      ...focused.lines.map(node => node.id),
    ]);
    return this.scene().branches
      .filter(branch => ids.has(branch.id))
      .map(branch => ({ branch, text: this.lineTexts().find(entry => entry.id === branch.id)! }));
  });

  /** Kleurcirkels in de focus die hun cijfer moeten houden. */
  protected readonly focusColours = computed(() => {
    const focused = this.focus();
    if (focused === undefined) return [];
    const ids = new Set([
      ...focused.ancestors.filter(node => node.layer === 2).map(node => node.id),
      ...focused.colours.map(node => node.id),
    ]);
    return this.scene().branches
      .flatMap(branch => branch.colours)
      .filter(colour => ids.has(colour.id));
  });

  /**
   * Cijfers in de toon- en basis-bolletjes. Die passen pas bij voldoende zoom,
   * dus ze verschijnen op rendergrootte — en enkel voor wat in beeld is, zodat
   * er nooit 1296 tekstelementen tegelijk in de DOM staan.
   */
  protected readonly deepNumbers = computed(() => {
    const width = this.hostWidth();
    if (width === 0) return [];
    const span = this.currentSpan();
    const pxPerUnit = width / span;
    const view = this.view();
    const half = span / 2;
    const cx = view?.cx ?? 0;
    const cy = view?.cy ?? 0;

    return this.layout().nodes.filter(node => {
      if (node.layer < 3) return false;
      if (node.radius * 2 * pxPerUnit < HdGateGraphComponent.MIN_DEEP_NUMBER_PX) return false;
      return Math.abs(node.x - cx) <= half + node.radius
        && Math.abs(node.y - cy) <= half + node.radius;
    }).map(node => ({
      id: node.id,
      num: node.num!,
      x: round(node.x),
      y: round(node.y + node.radius * 0.42),
      size: round(node.radius * 1.15),
    }));
  });

  /**
   * Dezelfde cijfers, maar enkel voor de knopen die in de focus zitten — die
   * moeten onverzwakt bovenop de gedimde basistekening terugkomen.
   *
   * Het lidmaatschap volgt uit de pad-ids (`g.2.4.3.5`): een knoop hoort bij de
   * focus wanneer zijn id een prefix is van de gehoverde id (voorouder) of
   * andersom (nakomeling).
   */
  /**
   * Idem voor de chart-highlight: de overlay hertekent de opgelichte toon- en
   * basis-bolletjes bovenop de gedimde laag, en moet hun cijfer dus meenemen.
   */
  protected readonly highlightDeepNumbers = computed(() => {
    const highlighted = this.highlight().sidesById;
    if (highlighted.size === 0) return [];
    return this.deepNumbers().filter(item => highlighted.has(item.id));
  });

  protected readonly focusDeepNumbers = computed(() => {
    const focused = this.focus();
    if (focused === undefined) return [];
    const id = focused.id;
    return this.deepNumbers().filter(item =>
      id.startsWith(item.id) || item.id.startsWith(`${id}.`));
  });

  protected readonly highlightActive = computed(() =>
    this.highlightEnabled() && this.highlight().nodes.length > 0);

  /** Ids van de opgelichte knopen — stuurt welke inhoud onverzwakt hertekend wordt. */
  private readonly highlightedIds = computed(() => new Set(this.highlight().sidesById.keys()));

  protected readonly rootHighlighted = computed(() => this.highlightedIds().has('g'));

  /**
   * De opgelichte lijncirkels, met hun tekst. In highlight-modus zit de
   * basistekening in een gedimde groep; deze worden er onverzwakt bovenop
   * hertekend, want alleen de RAND hoort zwart of rood te zijn — de inhoud
   * moet gewoon leesbaar blijven.
   */
  protected readonly highlightedLines = computed(() => {
    const ids = this.highlightedIds();
    return this.scene().branches
      .map((branch, index) => ({ branch, text: this.lineTexts()[index] }))
      .filter(entry => ids.has(entry.branch.id));
  });

  /** Opgelichte kleurcirkels (laag 2), zodat hun cijfer leesbaar blijft. */
  protected readonly highlightedColours = computed(() => {
    const ids = this.highlightedIds();
    return this.scene().branches
      .flatMap(branch => branch.colours)
      .filter(colour => ids.has(colour.id));
  });

  /** Wat de knop doet, niet in welke staat hij staat. */
  protected readonly toggleLabel = computed(() =>
    this.highlightActive() ? this.showFullLabel() : this.showHighlightLabel());

  protected toggleHighlight(): void {
    this.highlightEnabled.update(enabled => !enabled);
  }

  /** `undefined` = volledig ingezoomd op de hele tekening. */
  private readonly view = signal<ViewBoxState | undefined>(undefined);

  protected readonly viewBox = computed(() => {
    const extent = this.scene().extent;
    const view = this.view();
    if (view === undefined) {
      return `${-extent} ${-extent} ${extent * 2} ${extent * 2}`;
    }
    const half = view.span / 2;
    return `${round(view.cx - half)} ${round(view.cy - half)} ${round(view.span)} ${round(view.span)}`;
  });

  protected readonly isZoomed = computed(() => this.view() !== undefined);

  /** Straal van de volle binnencirkel binnen de ring van laag 0 en 1. */
  protected readonly innerRadius = computed(() => ({
    gate: HD_GATE_GRAPH_LAYOUT.radius.gate - HD_GATE_GRAPH_LAYOUT.ringPadding,
    line: HD_GATE_GRAPH_LAYOUT.radius.line - HD_GATE_GRAPH_LAYOUT.ringPadding,
  }));

  /**
   * Planeet-activaties per lijn, met hun Astronomicon-glyph. Alleen de twee
   * echte zijden tellen; `'highlight'` is een editor-hulpstaat.
   */
  private readonly glyphEntries = computed(() =>
    this.activations()
      .filter(activation => activation.side === 'personality' || activation.side === 'design')
      .map(activation => {
        const config = getHdPlanetDisplayConfigByPlanetId(activation.planetId);
        return {
          key: activation.id,
          char: config?.astronomiconChar ?? '',
          color: SIDE_GLYPH_COLORS[activation.side] ?? '#0e0e0e',
          name: `${activation.planet} ${activation.gate}.${activation.line}`,
          line: activation.line,
        };
      })
      .filter(entry => entry.char.length > 0));

  /** Inhoud van de poortcirkel: center-icoon, nummer, keynotes en glyph-rij. */
  protected readonly rootText = computed(() => {
    const width = HD_GATE_GRAPH_LAYOUT.radius.gate * 1.5;
    const first = wrapLabel(this.keynote1(), FONT.gateKeynote1, width, 2);
    const second = wrapLabel(this.keynote2(), FONT.gateKeynote2, width, 2);
    const iconSize = this.centerIconUrl() !== undefined ? 17 : 0;
    // Ruimer dan de andere tussenruimtes: het icoon hoort duidelijk bóven het
    // nummer te zweven, niet eraan vast te plakken.
    const iconGap = iconSize > 0 ? 11 : 0;
    const lineHeight1 = FONT.gateKeynote1 * 1.2;
    const lineHeight2 = FONT.gateKeynote2 * 1.2;
    const entries = this.glyphEntries();
    const glyphHeight = entries.length > 0 ? FONT.gateGlyph + 11 : 0;
    const total = iconSize + iconGap + FONT.gateNumber + 3
      + first.length * lineHeight1 + second.length * lineHeight2 + glyphHeight;

    let y = -total / 2;
    const iconY = y;
    y += iconSize + iconGap;
    const numberY = y + FONT.gateNumber * 0.78;
    y += FONT.gateNumber + 3;

    const keynoteLines: TextLineVm[] = [];
    for (const text of first) {
      keynoteLines.push({ text, y: round(y + lineHeight1 * 0.75), size: FONT.gateKeynote1, dim: false });
      y += lineHeight1;
    }
    for (const text of second) {
      keynoteLines.push({ text, y: round(y + lineHeight2 * 0.75), size: FONT.gateKeynote2, dim: true });
      y += lineHeight2;
    }

    const glyphs = entries.length > 0
      ? layoutGlyphRow(entries, 0, y + FONT.gateGlyph + 5, FONT.gateGlyph)
      : [];

    return {
      iconSize,
      iconX: round(-iconSize / 2),
      iconY: round(iconY),
      number: `${this.gate()}`,
      numberY: round(numberY),
      keynoteLines,
      glyphs,
    };
  });

  /** Huidige spanwijdte van de viewBox; kleiner = verder ingezoomd. */
  private readonly currentSpan = computed(() =>
    this.view()?.span ?? this.scene().extent * 2);

  /** Breedte van de host in echte pixels, bijgehouden door een ResizeObserver. */
  private readonly hostWidth = signal(0);

  /**
   * Onder deze rendergrootte is de lijn-keynote niet meer leesbaar en valt hij
   * weg; hij blijft dan bereikbaar via de tooltip op de tak.
   */
  private static readonly MIN_KEYNOTE_PX = 8;

  /** Onder deze gerenderde diameter past er geen cijfer in een bolletje. */
  private static readonly MIN_DEEP_NUMBER_PX = 15;

  /**
   * Of de lijn-keynotes getoond worden. Hangt af van de WERKELIJKE rendergrootte,
   * dus zowel van de paneelbreedte als van de zoom — inzoomen in een smal
   * gedockt paneel laat ze alsnog verschijnen. Een container query zou dat niet
   * kunnen: zoomen verandert de viewBox, niet de containerbreedte.
   */
  protected readonly showLineKeynotes = computed(() => {
    const width = this.hostWidth();
    if (width === 0) return true;
    return FONT.lineKeynote * (width / this.currentSpan()) >= HdGateGraphComponent.MIN_KEYNOTE_PX;
  });

  /**
   * Inhoud van de zes lijncirkels. Zonder keynote staat het lijnnummer netjes
   * gecentreerd in de cirkel in plaats van hoog met een gat eronder.
   */
  protected readonly lineTexts = computed<readonly LineTextVm[]>(() => {
    const keynotes = this.lineKeynotes();
    const showKeynotes = this.showLineKeynotes();
    const glyphsByLine = new Map<number, typeof entries>();
    const entries = this.glyphEntries();
    for (const entry of entries) {
      const list = glyphsByLine.get(entry.line) ?? [];
      list.push(entry);
      glyphsByLine.set(entry.line, list);
    }

    return this.scene().branches.map(branch => {
      const lineNumber = branch.line.num!;
      const keynote = keynotes[lineNumber - 1] ?? '';
      const lineGlyphs = glyphsByLine.get(lineNumber) ?? [];
      const glyphHeight = lineGlyphs.length > 0 ? FONT.lineGlyph + 3 : 0;
      const wrapped = showKeynotes
        ? wrapLabelInCircle(keynote, FONT.lineKeynote, 3, lineCount => this.lineKeynoteWidths(lineCount, glyphHeight))
        : [];
      const layout = this.lineBlockLayout(wrapped.length, glyphHeight);

      const keynoteLines: TextLineVm[] = wrapped.map((text, row) => ({
        text,
        y: round(branch.line.y + layout.rowYs[row]),
        size: FONT.lineKeynote,
        dim: true,
      }));

      const glyphs = lineGlyphs.length > 0
        ? layoutGlyphRow(lineGlyphs, branch.line.x, branch.line.y + layout.glyphY, FONT.lineGlyph)
        : [];

      return {
        id: branch.id,
        x: round(branch.line.x),
        number: `.${lineNumber}`,
        numberY: round(branch.line.y + layout.numberY),
        keynoteLines,
        keynote,
        glyphs,
      };
    });
  });

  /**
   * Verticale opbouw van een lijncirkel, relatief tot zijn middelpunt: nummer,
   * keynote-regels en glyph-rij staan als één blok gecentreerd. Geeft de
   * basislijn van het nummer en van elke regel, en de y waarop de glyph-rij hangt.
   */
  private lineBlockLayout(rowCount: number, glyphHeight: number): {
    readonly numberY: number;
    readonly rowYs: readonly number[];
    readonly glyphY: number;
  } {
    const lineHeight = FONT.lineKeynote * 1.2;
    const gap = rowCount > 0 ? 2 : 0;
    const top = -(FONT.lineNumber + gap + rowCount * lineHeight + glyphHeight) / 2;
    const firstRow = top + FONT.lineNumber + gap;
    return {
      numberY: top + FONT.lineNumber * 0.78,
      rowYs: Array.from({ length: rowCount }, (_, row) => firstRow + row * lineHeight + lineHeight * 0.75),
      glyphY: firstRow + rowCount * lineHeight + FONT.lineGlyph,
    };
  }

  /**
   * Beschikbare breedte per keynote-regel als de keynote `rowCount` regels
   * telt: de koorde van de binnencirkel op de rand van de regel die het verst
   * van het middelpunt ligt (bovenkant van de hoofdletters of onderkant van de
   * staarten), min een marge aan elke kant. Zo krijgt een keynote op één regel
   * vlak onder het nummer de volle breedte ("Irresponsibility", poort 4.3),
   * terwijl de onderste van drie regels smaller blijft.
   */
  private lineKeynoteWidths(rowCount: number, glyphHeight: number): number[] {
    const size = FONT.lineKeynote;
    return this.lineBlockLayout(rowCount, glyphHeight).rowYs.map(baseline => {
      const reach = Math.max(
        Math.abs(baseline - size * RALEWAY_CAP_HEIGHT),
        Math.abs(baseline + size * RALEWAY_DESCENT),
      );
      return chordWidth(this.innerRadius().line, reach) - 2 * LINE_KEYNOTE_MARGIN;
    });
  }

  /** Scrollwiel zoomt naar de cursor toe. */
  protected onWheel(event: WheelEvent): void {
    event.preventDefault();
    const target = event.currentTarget as SVGSVGElement | null;
    if (target === null) return;
    const rect = target.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const extent = this.scene().extent;
    const current = this.view() ?? { cx: 0, cy: 0, span: extent * 2 };
    const fractionX = (event.clientX - rect.left) / rect.width;
    const fractionY = (event.clientY - rect.top) / rect.height;
    const pointerX = current.cx - current.span / 2 + fractionX * current.span;
    const pointerY = current.cy - current.span / 2 + fractionY * current.span;

    const factor = event.deltaY > 0 ? ZOOM.step : 1 / ZOOM.step;
    const full = extent * 2;
    const span = Math.min(full * ZOOM.max, Math.max(full * ZOOM.min, current.span * factor));

    this.view.set({
      cx: pointerX - (fractionX - 0.5) * span,
      cy: pointerY - (fractionY - 0.5) * span,
      span,
    });
  }

  private drag: { pointerX: number; pointerY: number; cx: number; cy: number } | undefined;
  private pendingPan: { x: number; y: number } | undefined;
  private panFrame = 0;

  protected onPointerDown(event: PointerEvent): void {
    const target = event.currentTarget as SVGSVGElement;
    const extent = this.scene().extent;
    const current = this.view() ?? { cx: 0, cy: 0, span: extent * 2 };
    this.drag = { pointerX: event.clientX, pointerY: event.clientY, cx: current.cx, cy: current.cy };
    target.setPointerCapture(event.pointerId);
  }

  /**
   * Pannen wordt per animatieframe verwerkt in plaats van per event: een
   * pointermove kan meermaals per frame vuren en elke verwerking zet een signal.
   */
  /**
   * Eén handler voor pannen én de hover-hit-test, allebei per animatieframe
   * verwerkt in plaats van per event: een pointermove kan meermaals per frame
   * vuren en elke verwerking zet een signal.
   */
  protected onPointerMove(event: PointerEvent): void {
    const target = event.currentTarget as SVGSVGElement;
    const rect = target.getBoundingClientRect();
    if (rect.width === 0) return;
    this.pendingPan = { x: event.clientX, y: event.clientY };
    if (this.panFrame !== 0) return;

    this.panFrame = requestAnimationFrame(() => {
      this.panFrame = 0;
      const pending = this.pendingPan;
      if (pending === undefined) return;
      const extent = this.scene().extent;
      const current = this.view() ?? { cx: 0, cy: 0, span: extent * 2 };
      const drag = this.drag;

      if (drag !== undefined) {
        const scale = current.span / rect.width;
        this.view.set({
          cx: drag.cx - (pending.x - drag.pointerX) * scale,
          cy: drag.cy - (pending.y - drag.pointerY) * scale,
          span: current.span,
        });
        return;
      }

      const x = current.cx - current.span / 2 + ((pending.x - rect.left) / rect.width) * current.span;
      const y = current.cy - current.span / 2 + ((pending.y - rect.top) / rect.height) * current.span;
      this.hoveredNode.set(findHdGateGraphNodeAt(this.layout(), x, y));
    });
  }

  protected onPointerLeave(): void {
    this.hoveredNode.set(undefined);
  }

  protected onPointerUp(event: PointerEvent): void {
    this.drag = undefined;
    const target = event.currentTarget as SVGSVGElement;
    if (target.hasPointerCapture(event.pointerId)) target.releasePointerCapture(event.pointerId);
  }

  protected resetView(): void {
    this.view.set(undefined);
  }

  constructor() {
    const host = inject(ElementRef<HTMLElement>).nativeElement as HTMLElement;
    this.hostWidth.set(host.clientWidth);
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width ?? 0;
        if (width > 0) this.hostWidth.set(width);
      });
      observer.observe(host);
      inject(DestroyRef).onDestroy(() => observer.disconnect());
    }
  }
}

const round = (value: number): number => Math.round(value * 100) / 100;
